/**
 * ./src/services/singleMonths.js
 *
 * File that contains axios middleware and functions.
 * Axios is a promise-based HTTP Client for node.js that allow us to communicate with the app's back end.
 *
 */

import { sendRequest } from "./sendRequest";

/**
 * Monthly Tweet Volume getter
 *
 * @param {string} countryCode Country code
 * @param {string} year Year from 2017 to current
 * @param {string} month Month code from 01 to 12
 * @returns  Response object from API
 */
export const getMonthlyTweetVolume = (countryCode, year, month) =>
  sendRequest(`/monthly-tweet-volume/${countryCode}/${year}/${month}`);

/**
 * Monthly news coverage getter
 * @param {string} countryCode Country code
 * @param {string} year Year from 2017 to current
 * @param {string} month Month code from 01 to 12
 * @returns
 */
export const getMonthlyNewsCoverage = (countryCode, year, month) =>
  sendRequest(`/monthly-news-coverage/${countryCode}/${year}/${month}`);

/**
 * Monthly conversation transformation getter
 *
 * @param {string} countryCode Country code
 * @param {string} year Year from 2017 to current
 * @param {string} month Month code from 01 to 12
 * @returns
 */
export const getMonthlyThematicEvolution = (countryCode, year, month) =>
  sendRequest(`/monthly-thematic-evolution/${countryCode}/${year}/${month}`);

/**
 * Monthly migratory sentiment getter
 *
 * @param {string} countryCode Country code
 * @param {string} year Year from 2017 to current
 * @param {string} month Month code from 01 to 12
 * @returns
 */
export const getMonthlyMigratorySentiment = (countryCode, year, month) =>
  sendRequest(`/monthly-migratory-sentiment/${countryCode}/${year}/${month}`);

/**
 * Monthly migratory sentiment by topic getter
 *
 * @param {string} countryCode Country code
 * @param {string} topic Topic
 * @param {string} year Year from 2017 to current
 * @param {string} month Month code from 01 to 12
 * @returns
 */
export const getMigratorySentimentByTopic = (countryCode, topic, year, month) =>
  sendRequest(
    `/monthly-migratory-sentiment-topic/${countryCode}/${topic}/${year}/${month}`
  );

/**
 * Monthly institutional accounts' reactions getter
 *
 * @param {string} countryCode Country code
 * @param {string} year Year from 2017 to current
 * @param {string} month Month code from 01 to 12
 * @returns
 */
export const getMonthlyInstitutionalAccReactions = (countryCode, year, month) =>
  sendRequest(
    `/monthly-institutional-accounts/${countryCode}/${year}/${month}`
  );

/**
 * Monthly xenophobic prevalence getter
 *
 * @param {string} countryCode Country code
 * @param {string} year Year from 2017 to current
 * @param {string} month Month code from 01 to 12
 * @returns
 */
export const getMonthlyXenophobicPrevalence = (countryCode, year, month) =>
  sendRequest(`/monthly-xeno-prevalence/${countryCode}/${year}/${month}`);

/**
 * Monthly xenophobic accounts getter
 *
 * @param {string} countryCode Country code
 * @param {string} year Year from 2017 to current
 * @param {string} month Month code from 01 to 12
 * @returns
 */
export const getMonthlyXenophobicAccounts = (countryCode, year, month) =>
  sendRequest(`/monthly-xeno-accounts/${countryCode}/${year}/${month}`);

/**
 * Monthly tweet reach getter
 * @param {string} countryCode Country code
 * @param {string} year Year from 2017 to current
 * @param {string} month Month code from 01 to 12
 * @returns
 */
export const getMonthlyTweetReach = (countryCode, year, month) =>
  sendRequest(`/monthly-tweet-reach/${countryCode}/${year}/${month}`);

// Yearly data

/**
 * Yearly news coverage getter
 *
 * @param {string} countryCode Country code
 * @param {string} year Year from 2017 to current
 * @returns
 */
export const getNewsYearlyCoverage = (countryCode, year) =>
  sendRequest(`/yearly-news-coverage/${countryCode}/${year}`);

/**
 * Year most common topic getter
 *
 * @param {string} countryCode Country code
 * @param {string} year Year from 2017 to current
 * @returns
 */
export const getMostCommonTopicYearly = (countryCode, year) =>
  sendRequest(`/yearly-most-common-topic/${countryCode}/${year}`);

/**
 * Yearly migratory sentiment getter
 *
 * @param {string} countryCode Country code
 * @param {string} year Year from 2017 to current
 * @returns
 */
export const getYearlySentiment = (countryCode, year) =>
  sendRequest(`/yearly-sentiment/${countryCode}/${year}`);

/**
 * Yearly sentiment by topic getter
 *
 * @param {string} countryCode Country code
 * @param {string} topic Topic
 * @param {string} year Year from 2017 to current
 * @returns
 */
export const getYearlySentimentByTopic = (countryCode, topic, year) =>
  sendRequest(`/yearly-sentiment-topic/${countryCode}/${topic}/${year}`);

/**
 * Yearly xenophobic prevalence getter
 *
 * @param {string} countryCode Country code
 * @param {string} year Year from 2017 to current
 * @returns
 */
export const getYearlyXenophobicPrevalence = (countryCode, year) =>
  sendRequest(`/yearly-xenophobic-prevalence/${countryCode}/${year}`);

/**
 * Yearly xenophobic accounts getter
 *
 * @param {string} countryCode Country code
 * @param {string} year Year from 2017 to current
 * @returns
 */
export const getYearlyXenophobicAccounts = (countryCode, year) =>
  sendRequest(`/yearly-xenophobic-accounts/${countryCode}/${year}`);

/**
 * Yearly institutional accounts' reactions getter
 *
 * @param {string} countryCode Country code
 * @param {string} year Year from 2017 to current
 * @returns
 */
export const getYearlyInstitutionalAccountsReactions = (countryCode, year) =>
  sendRequest(`/yearly-institutional-accounts/${countryCode}/${year}`);

/**
 * Yearly topic percentage getter
 * @param {string} countryCode Country code
 * @param {string} year Year from 2017 to current
 * @param {string} topic Topic
 * @returns
 */
export const getYearlyTopicPercentage = (countryCode, year, topic) => {
  return sendRequest(
    `/yearly-topic-percentage/${countryCode}/${year}/${topic}`
  );
};
